import type { ReactNode } from 'react';
import { FileText } from 'lucide-react';
import { Pastille } from '../ui/Primitives';
import {
  dateReunion,
  jourCourt,
  type DepotFormateur,
  type PieceFormateur,
} from '../../lib/useFormateurs';

export const CLASSE_ACTION_LIGNE =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]';

/** Ce que porte un dépôt, en une ligne : date de dépôt, validité, vérification. */
function resumeDepot(depot: DepotFormateur): string {
  const morceaux = [`Déposé le ${dateReunion(depot.deposeLe)}`];
  if (depot.delivreLe) morceaux.push(`date de délivrance ${jourCourt(depot.delivreLe)}`);
  if (depot.finValidite) morceaux.push(`valable jusqu’au ${jourCourt(depot.finValidite)}`);
  if (depot.verifieLe) {
    morceaux.push(`vérifié le ${dateReunion(depot.verifieLe)}${depot.verifiePar ? ` par ${depot.verifiePar}` : ''}`);
  }
  return morceaux.join(' · ');
}

/**
 * Pièces obligatoires du formateur, une ligne par pièce et ses dépôts
 * dessous. L'état vient de l'API — péremption comprise — ; les gestes sont
 * fournis par l'écran : déposer pour le formateur, vérifier pour le pôle.
 */
export function PiecesFormateur({
  pieces,
  actionsPiece,
  actionsDepot,
}: {
  pieces: PieceFormateur[];
  actionsPiece?: (piece: PieceFormateur) => ReactNode;
  actionsDepot: (depot: DepotFormateur, piece: PieceFormateur) => ReactNode;
}): JSX.Element {
  return (
    <ul className="divide-y divide-gray-100">
      {pieces.map((piece) => (
        <li key={piece.type} className="px-4 py-3.5 sm:px-6">
          <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap">
            <FileText
              className={`mt-0.5 h-6 w-6 shrink-0 ${
                piece.etatTon === 'danger' ? 'text-red-400' : 'text-codialis-teal'
              }`}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1 basis-40">
              <div className="text-sm font-medium text-codialis-navy">
                {piece.libelle}
                {!piece.exigee && (
                  <span className="ml-2 text-[11px] font-normal text-codialis-gray">(plus demandée)</span>
                )}
              </div>
              <div className="text-[11px] text-codialis-gray">{piece.detail}</div>
              {piece.precision && (
                <div
                  className={`mt-0.5 text-[11px] ${
                    piece.etatTon === 'danger' ? 'font-medium text-red-700' : 'text-codialis-gray'
                  }`}
                >
                  {piece.precision}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {actionsPiece?.(piece)}
              <Pastille texte={piece.etatLibelle} ton={piece.etatTon} />
            </div>
          </div>

          {piece.depots.length > 0 && (
            <ul className="mt-2 space-y-1.5 sm:ml-9">
              {piece.depots.map((depot) => (
                <li
                  key={depot.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50 px-3 py-2"
                >
                  <div className="min-w-0 flex-1 basis-48">
                    <div className="truncate text-xs font-medium text-codialis-navy">{depot.nomFichier}</div>
                    <div className="text-[11px] text-codialis-gray">{resumeDepot(depot)}</div>
                    {depot.motifRefus && (
                      <div className="text-[11px] font-medium text-red-700">Motif : {depot.motifRefus}</div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {actionsDepot(depot, piece)}
                    <Pastille texte={depot.statutLibelle} ton={depot.statutTon} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
